import React, { useEffect, useState } from 'react'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import { useHistory } from 'react-router-dom'
// import component 👇
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import moment from 'moment'
import { IoArrowForward, IoArrowBack } from 'react-icons/io5'
import { IoMdCheckmarkCircle } from 'react-icons/io'
import { FaRegFile } from 'react-icons/fa'
import { FiDownload } from 'react-icons/fi'
import { getFileNameFromUrl } from '../../utils'
import FarmLeafletMap from '../FarmLeafletMap'

const FarmerDetailDrawer = ({
  isOpen,
  setIsOpen,
  farmer,
  showOnlyFarm = false,
}) => {
  const history = useHistory()
  const [tab, setTab] = useState("farmer_detail") // farmer_detail | farm_detail
  const [drawerWidth, setDrawerWidth] = useState(650)

  const exportToGeoJSON = (exportAll = false) => {
    const farms = farmer?.my_farms || []
    const dataToExport = exportAll ? farms : [farms[0]]

    const features = dataToExport.map(farm => {
      let geometry = null

      // Check if farm has mapped coordinates (polygon)
      if (farm?.is_mapped && farm?.mapped_coordinates) {
        const coords = farm?.mapped_coordinates
        const points = []

        // Get all point keys that start with 'point' followed by a letter
        const pointKeys = Object.keys(coords).filter(key => {
          if (!key?.startsWith('point')) return false
          if (key?.length !== 6) return false // 'point' + 1 letter = 6 characters
          if (key?.includes('To')) return false // Exclude distance keys
          return /^point[A-Z]$/.test(key) // Must be pointA, pointB, etc.
        })

        // Sort the keys to maintain proper order (A, B, C, D, etc.)
        pointKeys?.sort((a, b) => a.charCodeAt(5) - b.charCodeAt(5))

        pointKeys?.forEach(key => {
          const point = coords[key]
          if (point) {
            const lat = parseFloat(point?.latitude)
            const lng = parseFloat(point?.longitude)
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              points.push([lng, lat]) // GeoJSON uses [longitude, latitude]
            }
          }
        })

        // Close the polygon by adding the first point at the end
        if (points.length >= 3) {
          points.push(points[0])
        }

        if (points.length >= 4) {
          geometry = {
            type: "Polygon",
            coordinates: [points]
          }
        }
      }

      // If no mapped coordinates, use farm location as point
      if (!geometry && farm?.latitude && farm?.longitude) {
        geometry = {
          type: "Point",
          coordinates: [parseFloat(farm?.longitude), parseFloat(farm?.latitude)]
        }
      }

      return {
        type: "Feature",
        properties: {
          id: farm?.id,
          farm_name: farm?.farm_name || "Unknown Farm",
          farmer_name: `${farmer?.firstname || ''} ${farmer?.lastname || ''}`.trim() || "Unknown Farmer",
          farmer_email: farmer?.email || "",
          farmer_phone: farmer?.phone || "",
          region: farm?.region || "",
          district: farm?.district || "",
          community: farm?.community || "",
          crop_types: farm?.primary_crop || "",
          is_active: farm?.is_active !== false,
          area: farm?.farm_size ? `${farm?.farm_size} acres` : "Unknown",
          ownership_type: farm?.ownership_type || "",
          production_capacity: farm?.production_capacity || "",
          is_mapped: farm?.is_mapped || false,
          mapped_type: farm?.is_mapped && farm?.mapped_coordinates ? "polygon" : "point"
        },
        geometry: geometry
      }
    }).filter(feature => feature.geometry !== null)

    const geoJSON = {
      type: "FeatureCollection",
      features: features,
      properties: {
        name: exportAll ? "All Farms Export" : "Individual Farm Export",
        description: exportAll
          ? `Exported ${features.length} farms for farmer ${farmer?.firstname || ''} ${farmer?.lastname || ''}`.trim()
          : `Exported farm "${farms[0]?.farm_name}" for farmer ${farmer?.firstname || ''} ${farmer?.lastname || ''}`.trim(),
        export_date: new Date().toISOString().split('T')[0],
        total_farms: features.length,
        farmer_info: {
          farmer_name: `${farmer?.firstname || ''} ${farmer?.lastname || ''}`.trim(),
          farmer_email: farmer?.email || "",
          farmer_phone: farmer?.phone || ""
        }
      }
    }

    // Create and download the file
    const dataStr = JSON.stringify(geoJSON, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)

    const exportFileDefaultName = exportAll
      ? `farmer_farms_export_${new Date().toISOString().split('T')[0]}.geojson`
      : `farm_${farms[0]?.farm_name?.replace(/[^a-zA-Z0-9]/g, '_')}_export_${new Date().toISOString().split('T')[0]}.geojson`

    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 700) {
        setDrawerWidth(window.innerWidth - 20) // 20px margin
      } else {
        setDrawerWidth(650)
      }
    }
    window.addEventListener("resize", handleResize)
    handleResize() // set initial size
    return () => window.removeEventListener("resize", handleResize)
  }, []);

  useEffect(() => {
    if (showOnlyFarm)
      setTab("farm_detail")
  }, [showOnlyFarm])

  return (
    <Drawer
      open={isOpen}
      onClose={() => setIsOpen(false)}
      direction="right"
      size={drawerWidth}
    >
      <div className='w-full h-[91vh] overflow-y-auto dark:bg-dark'>
        <div className='w-full h-full'>
          <div className='p-4 w-full flex flex-row items-center justify-between'>
            <h1 className='text-xl font-bold dark:text-white'>Farmer Details</h1>

            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <AiOutlineCloseCircle className="h-7 w-7 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" />
            </button>
          </div>

          <div className='w-full'>
            <div className='mt-5 border-b border-gray-200 dark:border-border-dark w-full'>
              {
                [
                  {
                    label: "Farmer Details",
                    value: "farmer_detail"
                  },
                  {
                    label: "Farm Details",
                    value: "farm_detail"
                  }
                ].map((item) => {
                  return <button
                    disabled={showOnlyFarm}
                    className={`px-4 border-b-2 pb-3 text-base transition-all duration-300 ${tab === item.value ? "text-yellowcolor border-yellowcolor" : "text-gray-700 border-transparent"}`}
                    onClick={() => setTab(item.value)}>
                    {item.label}
                  </button>
                })
              }
            </div>


            <div className='w-full mt-5'>
              {
                tab === "farmer_detail"
                  ? <FarmerDetail farmer={farmer} />
                  : <FarmDetail farms={farmer?.my_farms || []} />
              }
            </div>
          </div>


        </div>
      </div>

      <div className='border-t border-gray-200 dark:bg-dark dark:border-border-dark w-full p-4'>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          {/* Export Buttons - Only show in farm detail tab */}
          {tab === "farm_detail" && farmer?.my_farms?.some((_farm) => _farm?.is_mapped) && (
            <div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-2'>
              <button
                onClick={() => exportToGeoJSON(false)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-secondaryDark dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
                title="Export current farm as GeoJSON"
              >
                <FiDownload size={16} />
                Export Farm
              </button>

              {farmer?.my_farms?.length > 1 && (
                <button
                  onClick={() => exportToGeoJSON(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium transition-colors"
                  title="Export all farms as GeoJSON"
                >
                  <FiDownload size={16} />
                  Export All Farms
                </button>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 lg:gap-4">
            <button
              onClick={() => setIsOpen(false)}
              className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'>
              Close
            </button>

            {!showOnlyFarm && <button
              onClick={() => history.push("/admin/updatefarmer", { farmer: farmer })}
              className='px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg'>
              Edit
            </button>}
          </div>
        </div>
      </div>
    </Drawer>
  )
}

const FarmDetail = ({ farms }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const farm = farms?.[currentIndex]
  const [selectedFarmId, setSelectedFarmId] = useState(null)

  // Update selected farm ID when current index changes or farm changes
  useEffect(() => {
    if (farm?.id) {
      setSelectedFarmId(farm.id)
    } else if (farms?.length > 0 && farms[0]?.id) {
      // Fallback to first farm if current farm doesn't have ID
      setSelectedFarmId(farms[0].id)
    }
  }, [currentIndex, farm?.id, farms])


  return <div className='w-full p-5 -mt-4 space-y-7'>
    <div className='flex flex-row items-center justify-between'>
      <h1 className='text-lg font-bold text-yellowcolor'>
        Farm(s): {currentIndex + 1} of {farms?.length}
      </h1>

      <div className='flex flex-row items-center gap-2'>
        <button
          className={`underline flex flex-row items-center gap-2 ${currentIndex > 0 ? 'text-yellowcolor' : 'text-gray-400 cursor-not-allowed'}`}
          onClick={() => {
            if (currentIndex > 0) {
              setCurrentIndex(currentIndex - 1)
            }
          }}
          disabled={currentIndex === 0}
        >
          <IoArrowBack />
          Previous
        </button>
        <button
          className={`underline flex flex-row items-center gap-2 ${currentIndex < farms?.length - 1 ? 'text-yellowcolor' : 'text-gray-400 cursor-not-allowed'}`}
          onClick={() => {
            if (currentIndex < farms?.length - 1) {
              setCurrentIndex(currentIndex + 1)
            }
          }}
          disabled={currentIndex === farms?.length - 1}
        >
          Next
          <IoArrowForward />
        </button>
      </div>
    </div>

    <div className='w-full p-5 space-y-7  transition-all duration-300'>
      <div className='grid md:grid-cols-3 gap-8'>
        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Farm Name</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.farm_name || "N/A"}
          </p>
        </div>

        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Farm Size (Acres)</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.farm_size || "N/A"}
          </p>
        </div>

        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Region</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.region || "N/A"}
          </p>
        </div>

        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>District</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.district || "N/A"}
          </p>
        </div>

        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Community</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.community || "N/A"}
          </p>
        </div>

        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Primary Commodity</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.primary_crop || "N/A"}
          </p>
        </div>


        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Other Crops</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.crop_types && Array.isArray(farm.crop_types) && farm.crop_types.length > 0
              ? farm.crop_types.map(crop => crop?.name).filter(Boolean).join(", ")
              : "N/A"}
          </p>
        </div>

        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Ownership Type</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.ownership_type}
          </p>
        </div>

        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Latitude</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.latitude || "N/A"}
          </p>
        </div>

        <div className="space-y-2">
          <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Longitude</label>
          <p className='text-sm capitalize font-medium text-black dark:text-white'>
            {farm?.longitude || "N/A"}
          </p>
        </div>
      </div>

      {farm?.images?.length > 0 && <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Farm Image</label>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {farm?.images?.map((image, index) => {
            return <div key={image?.id} className="w-full h-36 rounded-lg overflow-hidden">
              <img src={image?.image} alt="farm image" className='w-full h-full object-fill' />
            </div>
          })}
        </div>
      </div>}

      {farm?.farm_document && <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Farm Documents</label>

        <div
          className={`bg-white dark:bg-secondaryDark text-slate-500 font-semibold text-base border border-yellowcolor w-full flex flex-row gap-4 rounded-xl p-3 mx-auto`}>
          <div className="w-12 h-12 bg-yellowcolor/40 rounded-full flex items-center justify-center">
            <div className="w-8 h-8 bg-yellowcolor/50 text-yellowcolor rounded-full flex items-center justify-center">
              <FaRegFile />
            </div>
          </div>

          <div className="w-full flex-1">
            <div className="flex flex-row justify-between w-full">
              <div className="flex-1">
                <p className="text-sm font-bold text-black dark:text-white truncate">
                  {getFileNameFromUrl(farm?.farm_document)}
                </p>
              </div>

              <IoMdCheckmarkCircle className="text-yellowcolor" />
            </div>

            <div className="flex mt-4 flex-row items-center gap-2">
              <div className="w-full h-1.5 bg-yellowcolor rounded-full"></div>
              <p className="text-sm font-medium text-black dark:text-white">
                100%
              </p>
            </div>
          </div>


        </div>

      </div>}

      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Farm(s) On Map</label>
        <div style={{ height: '500px', width: '100%' }} className="rounded-lg overflow-hidden border border-gray-200 dark:border-border-dark">
          <FarmLeafletMap
            farms={farms}
            selectedFarmId={selectedFarmId}
            onMarkerClick={setSelectedFarmId}
          />
        </div>
      </div>
    </div>
  </div>
}

const FarmerDetail = ({ farmer }) => {
  return <div className='w-full p-5 space-y-7  -mt-4'>
    <div className="space-y-2">
      <label className='text-sm font-medium dark:text-white'>Profile Image</label>
      {farmer?.profile_image ? <img
        src={farmer?.profile_image}
        alt={farmer?.id}
        className='ml-10 w-24 h-24 rounded-full' /> : <div className='ml-10 w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center'>
        <span className='text-2xl uppercase font-medium'>
          {farmer?.firstname?.charAt(0)} {farmer?.lastname?.charAt(0)}
        </span>
      </div>}
    </div>

    <div className='grid md:grid-cols-2 gap-8'>
      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>First Name</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {farmer?.firstname}
        </p>
      </div>

      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Last Name</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {farmer?.lastname}
        </p>
      </div>

      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Phone Number</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {farmer?.phone || "N/A"}
        </p>
      </div>

      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Email</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {farmer?.email || "N/A"}
        </p>
      </div>


      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Level of education</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {farmer?.education_level || "N/A"}
        </p>
      </div>

      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Region</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {farmer?.region || "N/A"}
        </p>
      </div>

      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>District</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {farmer?.district || "N/A"}
        </p>
      </div>

      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Community</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {farmer?.community || "N/A"}
        </p>
      </div>

      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Gender</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {farmer?.gender || "N/A"}
        </p>
      </div>


      <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Date of Birth</label>
        <p className='text-sm capitalize font-medium text-black dark:text-white'>
          {moment(farmer?.dob).format("DD MMM, YYYY") || "N/A"}
        </p>
      </div>

      {farmer?.ghana_card_image_front && <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Ghana Card Front Image</label>
        <img src={farmer?.ghana_card_image_front} alt="ghana card front image" className='w-full h-36 object-fill' />
      </div>}

      {farmer?.ghana_card_image_back && <div className="space-y-2">
        <label className='text-sm font-medium text-gray-600 dark:text-border-dark'>Ghana Card Front Image</label>
        <img src={farmer?.ghana_card_image_back} alt="ghana card front image" className='w-full h-36 object-fill' />
      </div>}
    </div>

  </div>
}



export default FarmerDetailDrawer